import React, { useContext } from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Plus, LogOut, ShieldCheck, Truck, Stethoscope,
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { PartnerPortalProvider, usePartnerPortal } from './PartnerPortalContext';


function LayoutInner() {
  const navigate = useNavigate();
  const { logoutauth } = useContext(AuthContext);
  const { who, loading, error } = usePartnerPortal();

  const handleLogout = () => {
    logoutauth();
    navigate('/partner-portal/login', { replace: true });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 dark:text-gray-400">Loading portal…</div>;
  }

  if (error || !who?.kind) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 text-center border border-gray-100 dark:border-gray-700">
          <ShieldCheck className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Not authorised</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {error || 'Your account is not linked to any referring doctor or ambulance partner.'}
          </p>
          <button
            onClick={handleLogout}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const isAmbulance = who.kind === 'ambulance';
  const KindIcon = isAmbulance ? Truck : Stethoscope;
  const kindLabel = isAmbulance ? 'Ambulance Partner' : 'Referring Doctor';

  const NAV = [
    { to: '/partner-portal/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    isAmbulance
      ? { to: '/partner-portal/pickups', icon: Users, label: 'My Pickups' }
      : { to: '/partner-portal/referrals', icon: Users, label: 'My Referrals' },
    isAmbulance
      ? { to: '/partner-portal/pickups/new', icon: Plus, label: 'Record Pickup' }
      : { to: '/partner-portal/referrals/new', icon: Plus, label: 'Add Referral' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-tight">Partner Portal</span>
          </div>
        </div>

        <div className="p-3 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-sm ${
              isAmbulance
                ? 'bg-gradient-to-br from-orange-500 to-red-600'
                : 'bg-gradient-to-br from-purple-500 to-indigo-600'
            }`}>
              <KindIcon className="w-4.5 h-4.5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{who.name || 'Partner'}</div>
              <div className="text-[10px] uppercase text-gray-500 dark:text-gray-400 tracking-wide font-medium">{kindLabel}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-1">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to.endsWith('dashboard')}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}


export default function PartnerPortalLayout() {
  // Session gate — before spinning up context, ensure it's a third_party user
  const token = sessionStorage.getItem('token');
  const role = sessionStorage.getItem('login_role');
  if (!token) return <Navigate to="/partner-portal/login" replace />;
  if (role && role !== 'third_party') return <Navigate to="/dashboard" replace />;

  return (
    <PartnerPortalProvider>
      <LayoutInner />
    </PartnerPortalProvider>
  );
}
